import {
  HStack,
  Flex,
} from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import getCurrencyValue from 'lib/getCurrencyValue';
import { useI18n } from 'lib/i18n/context';
import AddressFromTo from 'ui/shared/address/AddressFromTo';
import Skeleton from 'ui/shared/chakra/Skeleton';
import Tag from 'ui/shared/chakra/Tag';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import ListItemMobile from 'ui/shared/ListItemMobile/ListItemMobile';
import FinanceStatusTag from 'ui/shared/statusTag/FinanceStatusTag';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';

type Props = {
  tx: Transaction;
  showBlockInfo: boolean;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  hideValueUsd?: boolean;
};

const TxsListItem = ({ tx, isLoading, currentAddress, enableTimeIncrement, hideValueUsd }: Props) => {
  const { t } = useI18n();
  const dataTo = tx.to ? tx.to : tx.created_contract;

  const amountStr = getCurrencyValue({ value: tx.value, decimals: '18', accuracy: 8 }).valueStr;

  return (
    <ListItemMobile display="block" width="100%" isAnimated key={ tx.hash }>
      <Flex justifyContent="space-between" mt={ 4 }>
        <HStack flexWrap="wrap">
          { tx.status_label ? (
            <FinanceStatusTag status={ tx.status_label } isLoading={ isLoading }/>
          ) : (
            <TxStatus status={ tx.status } errorText={ tx.status === 'error' ? tx.result : undefined } isLoading={ isLoading }/>
          ) }
        </HStack>
        <TxAdditionalInfo tx={ tx } isMobile isLoading={ isLoading }/>
      </Flex>
      <Flex justifyContent="space-between" lineHeight="24px" mt={ 3 } alignItems="center">
        <TxEntity
          isLoading={ isLoading }
          hash={ tx.hash }
          truncation="constant_long"
          fontWeight="700"
        />
        <TimeAgoWithTooltip
          timestamp={ tx.timestamp }
          enableIncrement={ enableTimeIncrement }
          isLoading={ isLoading }
          color="text_secondary"
          fontWeight="400"
          fontSize="sm"
        />
      </Flex>
      <AddressFromTo
        from={ tx.from }
        to={ dataTo }
        current={ currentAddress }
        isLoading={ isLoading }
        mt={ 6 }
        fontWeight="500"
        noIcon
      />
      <Flex mt={ 3 } justifyContent="space-between">
        <Flex alignItems="center" gap={ 2 }>
          <Skeleton isLoaded={ !isLoading } display="inline-block" whiteSpace="pre">{ t('txs.amount') }</Skeleton>
          <Skeleton isLoaded={ !isLoading } color="text_secondary">
            { amountStr || '-' }
          </Skeleton>
        </Flex>
      </Flex>
      { tx.method && (
        <Flex mt={ 3 }>
          <Skeleton isLoaded={ !isLoading } display="inline-block" whiteSpace="pre">{ t('common.method') } </Skeleton>
          <Tag colorScheme="gray" isLoading={ isLoading } isTruncated>
            { tx.method }
          </Tag>
        </Flex>
      ) }
      <Flex mt={ 2 } justifyContent="space-between">
        { !hideValueUsd && (
          <Flex columnGap={ 2 }>
            <Skeleton isLoaded={ !isLoading } display="inline-block" whiteSpace="pre">{ t('txs.valueUsd') }</Skeleton>
            <Skeleton isLoaded={ !isLoading } color="text_secondary">
              { tx.value_usd || 'N/A' }
            </Skeleton>
          </Flex>
        ) }
        <Flex columnGap={ 2 } mb={ 3 }>
          <Skeleton isLoaded={ !isLoading } display="inline-block" whiteSpace="pre">{ t('txs.txnFee') }</Skeleton>
          <TxFee tx={ tx } isLoading={ isLoading }/>
        </Flex>
      </Flex>
    </ListItemMobile>
  );
};

export default React.memo(TxsListItem);
